/**
 * SearchableSelect — drop-in replacement for Chakra <Select> when the option
 * list is long enough to need filtering. Wraps `react-select` (already in
 * the bundle for cohort condition builder) with Chakra-flavoured styling so
 * it visually matches our existing form controls.
 *
 * Props mirror the most common <Select> usage:
 *   value          string                — selected option's value
 *   onChange       (value: string) => …  — fires with the picked option's value (or '' on clear)
 *   options        Array<{value, label}> — the choices
 *   placeholder    string                — empty-state text
 *   isClearable    boolean               — show ✕ to clear (default false)
 *   size           'xs' | 'sm' | 'md'    — visual size (default 'sm')
 *   isDisabled     boolean
 */
import React from 'react';
import ReactSelect from 'react-select';
import { PRIMARY_BG } from '../../../theme/brand';

const BRAND = PRIMARY_BG;
const BORDER = '#E2E8F0';
const TEXT = '#1A202C';

const SIZE = {
  xs: { minHeight: '24px', height: '24px', fontSize: 12, padX: 6 },
  sm: { minHeight: '32px', height: '32px', fontSize: 14, padX: 10 },
  md: { minHeight: '40px', height: '40px', fontSize: 14, padX: 12 },
};

function makeStyles(s) {
  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: s.minHeight,
      height: s.height,
      fontSize: s.fontSize,
      borderColor: state.isFocused ? BRAND : BORDER,
      boxShadow: state.isFocused ? `0 0 0 1px ${BRAND}` : 'none',
      '&:hover': { borderColor: state.isFocused ? BRAND : '#CBD5E0' },
      borderRadius: 6,
      backgroundColor: state.isDisabled ? '#F7FAFC' : 'white',
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: s.padX,
      paddingRight: 2,
      height: s.height,
    }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0, fontSize: s.fontSize, color: TEXT }),
    singleValue: (provided) => ({ ...provided, fontSize: s.fontSize, color: TEXT }),
    placeholder: (provided) => ({ ...provided, fontSize: s.fontSize, color: '#A0AEC0' }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (provided) => ({ ...provided, height: s.height }),
    dropdownIndicator: (provided) => ({ ...provided, padding: 4, color: '#718096' }),
    clearIndicator: (provided) => ({ ...provided, padding: 4, color: '#A0AEC0' }),
    // CRITICAL: when rendered inside a Chakra Drawer / Modal (z-index ~1500),
    // both the menu element AND the portal wrapper need a higher z-index or
    // the dropdown opens INVISIBLE behind the drawer. 99999 covers every
    // overlay layer the portal might be nested in.
    menu: (provided) => ({
      ...provided,
      fontSize: s.fontSize,
      zIndex: 99999,
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.18)',
      borderRadius: 6,
      backgroundColor: 'white',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 260,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? BRAND
        : state.isFocused ? 'var(--brand-tint)' : 'white',
      color: state.isSelected ? 'white' : TEXT,
      cursor: 'pointer',
      fontSize: s.fontSize,
      padding: '6px 10px',
    }),
  };
}

export default function SearchableSelect({
  value, onChange, options, placeholder, isClearable = false,
  size = 'sm', isDisabled = false, ...rest
}) {
  const sizeCfg = SIZE[size] || SIZE.sm;
  const opts = Array.isArray(options) ? options : [];
  const selected = opts.find((o) => o.value === value) || null;
  return (
    <ReactSelect
      value={selected}
      onChange={(picked) => onChange?.(picked ? picked.value : '')}
      options={opts}
      placeholder={placeholder || 'Pick…'}
      isClearable={isClearable}
      isDisabled={isDisabled}
      styles={makeStyles(sizeCfg)}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      {...rest}
    />
  );
}
